import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  App as AntApp, Button, Checkbox, Dropdown, Image, Input, Space, Table, Tooltip,
} from "antd";
import type { ColumnsType, ColumnType } from "antd/es/table";
import type { FilterValue, SorterResult, TableCurrentDataSource } from "antd/es/table/interface";
import {
  AppstoreOutlined,
  DatabaseOutlined,
  DownloadOutlined,
  FilterOutlined,
  ReloadOutlined,
  RightOutlined,
  SearchOutlined,
  SettingOutlined,
  SortAscendingOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { Resizable, type ResizeCallbackData } from "react-resizable";
import "react-resizable/css/styles.css";

import { api, ApiError } from "../api";
import { ScraperTag, StatusPill } from "../components/ScraperTag";
import type { JobDetail, OutputColumn, ScraperSchema } from "../types";

const POLL_MS = 1000;
type Row = Record<string, unknown>;

const FALLBACK_COLUMNS: OutputColumn[] = [
  { key: "sku", label: "SKU", width: 140 },
  { key: "name", label: "Name", ellipsis: true },
  { key: "price", label: "Price", width: 90, format: "number" },
];

const COL_VIS_KEY = (name: string) => `crl:colVis:${name}`;
const COL_WIDTH_KEY = (name: string) => `crl:colWidth:${name}`;

// Resizable <th> wrapper so users can drag column edges like EUI's data grid.
function ResizableTitle(
  props: React.HTMLAttributes<HTMLTableCellElement> & {
    width?: number; onResize?: (e: React.SyntheticEvent, data: ResizeCallbackData) => void;
  },
) {
  const { onResize, width, ...rest } = props;
  if (!width) return <th {...rest} />;
  return (
    <Resizable
      width={width}
      height={0}
      handle={
        <span
          className="react-resizable-handle"
          onClick={(e) => e.stopPropagation()}
        />
      }
      onResize={onResize}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th {...rest} />
    </Resizable>
  );
}

export function TaskPage() {
  const { taskId } = useParams<{ taskId: string }>();
  const { message } = AntApp.useApp();
  const navigate = useNavigate();
  const [job, setJob] = useState<JobDetail | null>(null);
  const [scrapers, setScrapers] = useState<ScraperSchema[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [visibleCols, setVisibleCols] = useState<Record<string, boolean>>({});
  const [colWidths, setColWidths] = useState<Record<string, number>>({});
  // Current state of what the table is showing after antd's built-in
  // filter/sort — lets downloads export exactly what the user sees.
  const [renderedRows, setRenderedRows] = useState<Row[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    api.scrapers().then(({ scrapers }) => setScrapers(scrapers)).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!taskId) return;
    let cancelled = false;
    // Result JSONB can exceed 200 MB for large scrapes. We fetch it once on
    // first load, then poll the cheap `?meta=1` variant for status/progress
    // updates. The full body is re-fetched only when the job status
    // transitions (e.g. running → done) — that's when new rows matter.
    let lastStatus: string | null = null;

    async function tick(firstLoad = false) {
      try {
        const fresh = firstLoad
          ? await api.getTask(taskId!)
          : await api.getTaskMeta(taskId!);
        if (cancelled) return;

        const statusChanged = lastStatus !== null && lastStatus !== fresh.status;

        if (firstLoad) {
          setJob(fresh);
        } else if (statusChanged) {
          // Terminal transition: pull the full body so the final table renders.
          const full = await api.getTask(taskId!);
          if (cancelled) return;
          setJob(full);
        } else {
          // Meta-only: keep existing rows, just refresh status/progress/error.
          setJob((prev) => prev
            ? { ...prev, status: fresh.status, progress: fresh.progress, error: fresh.error, finishedAt: fresh.finishedAt }
            : fresh);
        }
        lastStatus = fresh.status;
        setLoading(false);

        if (fresh.status === "queued" || fresh.status === "running") {
          timerRef.current = setTimeout(() => tick(false), POLL_MS);
        }
      } catch (err) {
        if (cancelled) return;
        setLoading(false);
        if (err instanceof ApiError && err.status === 404) message.error("Task not found.");
        else message.error(err instanceof Error ? err.message : "Failed to load");
      }
    }
    tick(true);
    return () => { cancelled = true; if (timerRef.current) clearTimeout(timerRef.current); };
  }, [taskId, message]);

  const schema = useMemo(
    () => scrapers.find((s) => s.name === job?.scraper),
    [scrapers, job?.scraper],
  );
  const outputColumns: OutputColumn[] = schema?.output_columns ?? FALLBACK_COLUMNS;

  // Load column visibility + widths from localStorage when scraper changes
  useEffect(() => {
    if (!schema) return;
    const savedVis = localStorage.getItem(COL_VIS_KEY(schema.name));
    if (savedVis) {
      try { setVisibleCols(JSON.parse(savedVis)); }
      catch { setVisibleCols(Object.fromEntries(outputColumns.map((c) => [c.key, true]))); }
    } else {
      setVisibleCols(Object.fromEntries(outputColumns.map((c) => [c.key, true])));
    }
    const savedWidths = localStorage.getItem(COL_WIDTH_KEY(schema.name));
    if (savedWidths) {
      try { setColWidths(JSON.parse(savedWidths)); } catch { setColWidths({}); }
    } else {
      setColWidths({});
    }
  }, [schema, outputColumns]);

  // Global text search across all fields (applied before antd's per-column filters).
  const searchFiltered: Row[] = useMemo(() => {
    const rows = (job?.result ?? []) as unknown as Row[];
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter((r) => matchesAnyField(r, q));
  }, [job?.result, search]);

  // Sync renderedRows when the upstream set changes (before antd filters apply).
  useEffect(() => { setRenderedRows(searchFiltered); }, [searchFiltered]);

  async function handleCancel() {
    if (!taskId) return;
    try { await api.cancelTask(taskId); message.info("Cancellation requested"); }
    catch { message.error("Cancel failed"); }
  }

  function handleDownload(format: "json" | "csv") {
    if (!job?.result || renderedRows.length === 0) return;
    const blob = format === "json"
      ? new Blob([JSON.stringify(renderedRows, null, 2)], { type: "application/json" })
      : new Blob([toCsv(renderedRows, outputColumns)], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const total = (job.result as unknown[]).length;
    const suffix = renderedRows.length < total ? `-filtered-${renderedRows.length}of${total}` : "";
    a.download = `${job.scraper}-${job.id.slice(0, 8)}${suffix}.${format}`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function toggleCol(key: string, v: boolean) {
    const next = { ...visibleCols, [key]: v };
    setVisibleCols(next);
    if (schema) localStorage.setItem(COL_VIS_KEY(schema.name), JSON.stringify(next));
  }

  function handleResize(key: string) {
    return (_e: React.SyntheticEvent, { size }: ResizeCallbackData) => {
      const width = Math.max(60, Math.round(size.width));
      setColWidths((prev) => {
        const next = { ...prev, [key]: width };
        if (schema) localStorage.setItem(COL_WIDTH_KEY(schema.name), JSON.stringify(next));
        return next;
      });
    };
  }

  const allRows = (job?.result ?? []) as unknown as Row[];
  const columns = useMemo(
    () => buildColumns(outputColumns, allRows, visibleCols, colWidths, handleResize),
    // handleResize is stable w.r.t. schema; safe to omit
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [outputColumns, allRows, visibleCols, colWidths],
  );

  function handleTableChange(
    _pagination: unknown,
    _filters: Record<string, FilterValue | null>,
    _sorter: SorterResult<Row> | SorterResult<Row>[],
    extra: TableCurrentDataSource<Row>,
  ) {
    setRenderedRows(extra.currentDataSource);
  }

  if (loading)
    return (
      <div style={{ textAlign: "center", padding: 40, color: "var(--text-tertiary)" }}>
        Loading…
      </div>
    );
  if (!job) return null;

  const totalItems = (job.result as unknown[] | undefined)?.length ?? 0;
  const visibleColCount = outputColumns.filter((c) => visibleCols[c.key] !== false).length;
  const resultsRows = job.result as Row[] | undefined;

  return (
    <>
      <div className="crl-page-top">
        <div
          className="crl-breadcrumb"
          style={{ cursor: "pointer" }}
          onClick={() => navigate("/output")}
        >
          <span>Tasks</span>
          <RightOutlined className="crl-chev" style={{ fontSize: 10 }} />
          <span className="crl-cur">#{job.seq ?? job.id.slice(0, 6)}</span>
        </div>
        <div className="crl-page-top-spacer" />
        {(job.status === "queued" || job.status === "running") && (
          <Button size="small" danger icon={<StopOutlined />} onClick={handleCancel}>
            Cancel
          </Button>
        )}
        {resultsRows && resultsRows.length > 0 && (
          <>
            <Tooltip
              title={
                renderedRows.length < totalItems
                  ? `Downloads ${renderedRows.length} filtered rows`
                  : `Downloads all ${totalItems} rows`
              }
            >
              <Button size="small" type="text" icon={<DownloadOutlined />} onClick={() => handleDownload("json")}>
                JSON
              </Button>
            </Tooltip>
            <Button size="small" type="text" icon={<DownloadOutlined />} onClick={() => handleDownload("csv")}>
              CSV
            </Button>
          </>
        )}
        <Button size="small" type="text" icon={<ReloadOutlined />}>
          Re-run
        </Button>
      </div>

      <div className="crl-detail-header">
        <div className="crl-detail-icon">
          <DatabaseOutlined />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="crl-detail-title-row">
            <h1 className="crl-detail-title">Task #{job.seq ?? job.id.slice(0, 6)}</h1>
            <StatusPill status={job.status} />
          </div>
          <div className="crl-detail-sub">
            <ScraperTag name={job.scraper} label={schema?.label ?? job.scraper} />
            <span className="crl-sep">·</span>
            <span>
              started <b>{new Date(job.startedAt).toLocaleString()}</b>
            </span>
          </div>
          {job.error && (
            <div style={{ marginTop: 8, color: "var(--accent-red)", fontSize: 12.5 }}>
              {job.error}
            </div>
          )}
        </div>
      </div>

      <div className="crl-detail-meta-grid">
        <div className="crl-meta-item">
          <div className="crl-k">Items</div>
          <div className="crl-v big">{job.progress.count.toLocaleString()}</div>
        </div>
        <div className="crl-meta-item">
          <div className="crl-k">Duration</div>
          <div className="crl-v big">
            {formatDuration((job.finishedAt ?? Date.now()) - job.startedAt)}
          </div>
        </div>
        <div className="crl-meta-item">
          <div className="crl-k">Rate</div>
          <div className="crl-v big">
            {formatRate(job.progress.count, (job.finishedAt ?? Date.now()) - job.startedAt)}
            <span className="crl-unit">/s</span>
          </div>
        </div>
        <div className="crl-meta-item">
          <div className="crl-k">Started</div>
          <div className="crl-v">{formatDateFull(job.startedAt)}</div>
        </div>
        <div className="crl-meta-item">
          <div className="crl-k">Finished</div>
          <div className="crl-v">
            {job.finishedAt ? formatDateFull(job.finishedAt) : <span className="crl-muted">—</span>}
          </div>
        </div>
      </div>

      {resultsRows && resultsRows.length > 0 ? (
        <>
          <div className="crl-detail-toolbar">
            <Input
              placeholder={`Search across ${totalItems.toLocaleString()} rows`}
              prefix={<SearchOutlined style={{ color: "var(--text-tertiary)" }} />}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
              style={{ maxWidth: 280, minWidth: 200, background: "var(--bg-secondary)" }}
              size="small"
            />
            <button className="crl-chip" disabled style={{ opacity: 0.55, cursor: "not-allowed" }}>
              <FilterOutlined /> Filter
            </button>
            <button className="crl-chip" disabled style={{ opacity: 0.55, cursor: "not-allowed" }}>
              <SortAscendingOutlined /> Sort
            </button>
            <Dropdown
              trigger={["click"]}
              placement="bottomRight"
              menu={{
                items: outputColumns.map((c) => ({
                  key: c.key,
                  label: (
                    <Checkbox
                      checked={visibleCols[c.key] !== false}
                      onChange={(e) => toggleCol(c.key, e.target.checked)}
                    >
                      {c.label}
                    </Checkbox>
                  ),
                })),
              }}
            >
              <button className="crl-chip">
                <SettingOutlined /> {visibleColCount} / {outputColumns.length} columns
              </button>
            </Dropdown>
            <div style={{ flex: 1 }} />
            <span className="crl-toolbar-info">
              Showing <b>{renderedRows.length.toLocaleString()}</b> of{" "}
              {totalItems.toLocaleString()}
            </span>
          </div>

          <div style={{ flex: 1, overflow: "auto", minHeight: 0 }}>
            <Table<Row>
              className="crl-results-table"
              columns={columns}
              components={{ header: { cell: ResizableTitle } }}
              dataSource={searchFiltered}
              rowKey={(r, i) =>
                `${String(r.sku ?? r.slug ?? r.source_id ?? r.guideid ?? r.pageid ?? "")}-${i}`
              }
              size="small"
              scroll={{ x: "max-content" }}
              onChange={handleTableChange}
              pagination={{
                pageSize: 25,
                showSizeChanger: true,
                pageSizeOptions: [10, 25, 50, 100, 250],
                showTotal: (total, range) => `${range[0]}–${range[1]} of ${total}`,
                size: "small",
              }}
            />
          </div>

          <div className="crl-footer-bar">
            <span>
              {totalItems.toLocaleString()} rows · {outputColumns.length} columns
            </span>
            <div className="crl-spacer" />
            <span>
              <AppstoreOutlined style={{ marginRight: 4 }} />
              {schema?.output_kind ?? "rows"}
            </span>
          </div>
        </>
      ) : job.status === "queued" || job.status === "running" ? (
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--text-secondary)",
            fontSize: 13,
            gap: 8,
          }}
        >
          <span className="crl-pulse" />
          Scraper {job.status} — {job.progress.count.toLocaleString()} items collected so far…
        </div>
      ) : (
        <div style={{ flex: 1 }} />
      )}
    </>
  );
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function formatRate(count: number, ms: number): string {
  if (ms <= 0 || count <= 0) return "0";
  const rate = count / (ms / 1000);
  if (rate >= 100) return rate.toFixed(0);
  if (rate >= 10) return rate.toFixed(1);
  return rate.toFixed(2);
}

function formatDateFull(ms: number): string {
  const d = new Date(ms);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

// ---- Column + cell builders ------------------------------------------------

function buildColumns(
  output: OutputColumn[],
  allRows: Row[],
  visible: Record<string, boolean>,
  widths: Record<string, number>,
  handleResize: (key: string) => (e: React.SyntheticEvent, d: ResizeCallbackData) => void,
): ColumnsType<Row> {
  return output
    .filter((c) => visible[c.key] !== false)
    .map((c): ColumnType<Row> => {
      const path = c.key.split(".");
      const effectiveWidth = widths[c.key] ?? c.width;
      const base: ColumnType<Row> = {
        title: c.label,
        dataIndex: path.length > 1 ? path : c.key,
        key: c.key,
        width: effectiveWidth,
        ellipsis: c.ellipsis ? { showTitle: false } : false,
        align: c.format === "number" ? ("right" as const) : undefined,
        onHeaderCell: () => ({
          width: effectiveWidth,
          onResize: handleResize(c.key),
        // antd types expect a concrete HTMLAttributes subtype; our ResizableTitle accepts HTMLAttributes of th.
        }) as unknown as React.HTMLAttributes<HTMLElement>,
      };

      // Sorting: skip for image/url (no meaningful order)
      if (c.format !== "image" && c.format !== "url") {
        base.sorter = (a, b) => compareCell(getPath(a, c.key), getPath(b, c.key), c.format);
        base.sortDirections = ["ascend", "descend"];
      }

      // Per-column filter
      if (c.format === "stock") {
        base.filters = uniqueValues(allRows, c.key).map((v) => ({ text: String(v), value: String(v) }));
        base.onFilter = (value, record) => String(getPath(record, c.key) ?? "") === value;
      } else if (c.format === "number") {
        // Number range via filterDropdown
        base.filterDropdown = numberRangeFilterDropdown(c.key);
        base.onFilter = (value, record) => {
          const [min, max] = (value as unknown) as [number | null, number | null];
          const v = Number(getPath(record, c.key));
          if (!Number.isFinite(v)) return false;
          if (min != null && v < min) return false;
          if (max != null && v > max) return false;
          return true;
        };
      } else if (c.format !== "url" && c.format !== "image") {
        // Text contains
        base.filterDropdown = textContainsFilterDropdown();
        base.onFilter = (value, record) => {
          const raw = getPath(record, c.key);
          const s = (typeof raw === "object" && raw !== null ? JSON.stringify(raw) : String(raw ?? "")).toLowerCase();
          return s.includes(String(value).toLowerCase());
        };
        base.filterIcon = (filtered) => (
          <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
        );
      }

      base.render = (v: unknown) => renderCell(v, c.format, c.key);
      return base;
    });
}

function getPath(obj: unknown, path: string): unknown {
  let cur: unknown = obj;
  for (const k of path.split(".")) {
    if (cur == null || typeof cur !== "object") return undefined;
    cur = (cur as Record<string, unknown>)[k];
  }
  return cur;
}

function renderCell(v: unknown, format: OutputColumn["format"] | undefined, key: string): React.ReactNode {
  if (v === null || v === undefined || v === "") {
    return <span style={{ color: "#cbd5e1" }}>—</span>;
  }

  if (format === "image" && typeof v === "string") {
    return <Image src={v} width={40} height={40} style={{ objectFit: "cover" }} preview={{ mask: "👁" }} />;
  }
  if (format === "url" && typeof v === "string") {
    return <a href={v} target="_blank" rel="noreferrer">open ↗</a>;
  }
  if (format === "stock") {
    const s = String(v);
    const muted = s !== "in stock";
    return <span style={{ color: muted ? "#94a3b8" : "#0f172a" }}>{s}</span>;
  }
  if (format === "number" && typeof v === "number") {
    return v.toLocaleString();
  }

  // Image thumbnail for *image_url* fields (even if schema forgot to mark format)
  if (typeof v === "string" && (key === "image_url" || key.endsWith("_image"))) {
    return <Image src={v} width={40} height={40} style={{ objectFit: "cover" }} preview={{ mask: "👁" }} />;
  }

  // Arrays of scalars → join as comma-separated text. Array-of-objects → count with JSON tooltip.
  if (Array.isArray(v)) {
    if (v.length === 0) return <span style={{ color: "#cbd5e1" }}>—</span>;
    if (v.every((x) => typeof x === "string" || typeof x === "number" || typeof x === "boolean")) {
      const joined = v.join(", ");
      const short = joined.length > 120 ? joined.slice(0, 120) + "…" : joined;
      return <Tooltip title={joined}><span>{short}</span></Tooltip>;
    }
    return <Tooltip title={JSON.stringify(v, null, 2)}><span>{v.length}</span></Tooltip>;
  }
  if (typeof v === "object") {
    const s = JSON.stringify(v);
    const short = s.length > 120 ? s.slice(0, 120) + "…" : s;
    return <Tooltip title={JSON.stringify(v, null, 2)}><span style={{ color: "#64748b" }}>{short}</span></Tooltip>;
  }

  const s = String(v);
  if (s.length > 80) {
    return <Tooltip title={s}><span>{s.slice(0, 80)}…</span></Tooltip>;
  }
  return s;
}

function matchesAnyField(row: Row, q: string): boolean {
  for (const v of Object.values(row)) {
    if (v === null || v === undefined) continue;
    const s = typeof v === "object" ? JSON.stringify(v) : String(v);
    if (s.toLowerCase().includes(q)) return true;
  }
  return false;
}

function compareCell(a: unknown, b: unknown, format: OutputColumn["format"] | undefined): number {
  if (format === "number") {
    const na = Number(a), nb = Number(b);
    return (Number.isFinite(na) ? na : -Infinity) - (Number.isFinite(nb) ? nb : -Infinity);
  }
  const sa = a == null ? "" : String(a);
  const sb = b == null ? "" : String(b);
  return sa.localeCompare(sb, undefined, { numeric: true });
}

function uniqueValues(rows: Row[], key: string): string[] {
  const set = new Set<string>();
  for (const r of rows) {
    const v = getPath(r, key);
    if (v != null && v !== "") set.add(String(v));
  }
  return Array.from(set).sort();
}

// ---- Filter dropdowns ------------------------------------------------------

function textContainsFilterDropdown(): ColumnType<Row>["filterDropdown"] {
  return ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
    <div style={{ padding: 8, minWidth: 200 }}>
      <Input
        autoFocus
        placeholder="Contains…"
        value={selectedKeys[0] as string}
        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
        onPressEnter={() => confirm()}
        style={{ marginBottom: 8, display: "block" }}
      />
      <Space>
        <Button type="primary" size="small" onClick={() => confirm()}>Filter</Button>
        <Button size="small" onClick={() => { clearFilters?.(); confirm(); }}>Clear</Button>
      </Space>
    </div>
  );
}

function numberRangeFilterDropdown(_key: string): ColumnType<Row>["filterDropdown"] {
  return ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
    const [min, max] = ((selectedKeys[0] as unknown) as [number | null, number | null] | undefined) ?? [null, null];
    const update = (nextMin: number | null, nextMax: number | null) => {
      if (nextMin == null && nextMax == null) setSelectedKeys([]);
      else setSelectedKeys([[nextMin, nextMax] as unknown as React.Key]);
    };
    return (
      <div style={{ padding: 8, minWidth: 200 }}>
        <Input
          type="number"
          placeholder="Min"
          value={min ?? ""}
          onChange={(e) => update(e.target.value === "" ? null : Number(e.target.value), max)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Input
          type="number"
          placeholder="Max"
          value={max ?? ""}
          onChange={(e) => update(min, e.target.value === "" ? null : Number(e.target.value))}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button type="primary" size="small" onClick={() => confirm()}>Filter</Button>
          <Button size="small" onClick={() => { clearFilters?.(); confirm(); }}>Clear</Button>
        </Space>
      </div>
    );
  };
}

// ---- CSV export (respects current column order + only visible columns) ----

function toCsv(rows: Row[], cols: OutputColumn[]): string {
  if (rows.length === 0) return "";
  // Declared columns first (dot-paths resolved); then append top-level keys that
  // aren't referenced by any declared column, so the CSV stays a superset of the raw data.
  const declared = cols.map((c) => c.key);
  const topLevelReferenced = new Set(declared.map((k) => k.split(".")[0]!));
  const extras = Array.from(
    new Set(rows.flatMap((r) => Object.keys(r))),
  ).filter((k) => !topLevelReferenced.has(k));
  const keys = [...declared, ...extras];
  return [
    keys.join(","),
    ...rows.map((r) => keys.map((k) => csvCell(getPath(r, k))).join(",")),
  ].join("\n");
}

function csvCell(v: unknown): string {
  if (v === null || v === undefined) return "";
  const s = typeof v === "object" ? JSON.stringify(v) : String(v);
  return (s.includes(",") || s.includes("\n") || s.includes('"'))
    ? `"${s.replace(/"/g, '""')}"` : s;
}
